import { useEffect, useMemo, useRef, useState } from "react";

// Skriptad illustration av regelchatten (agent-krav 14.9) för startsidan. Inga anrop, påhittade värden.
// Ekonomen skriver, agenten frågar och lägger ett förslag. Förslaget godkänns i styrpanelen, aldrig av agenten.
// Alla meddelanden ritas från början men osynliga, så panelen har sin slutliga höjd och sidan inte hoppar.
// Reducerad rörelse visar hela samtalet direkt.

type Rad = { roll: "ekonom" | "agent"; text: string };

const LEV = "Nordvik Fastighetsservice AB";
const RADER: Rad[] = [
  { roll: "ekonom", text: "Nordvik Fastighetsservice ska från 1 november bokas på 5170 och fördelas 60/40 mellan kostnadsställe 110 och 120." },
  { roll: "agent", text: `Jag hittar ${LEV}. 5170 Reparation och underhåll av fastighet finns i kontoplanen. Ska regeln gälla alla fakturor från leverantören, eller bara vissa projekt?` },
  { roll: "ekonom", text: "Alla fakturor. Belopp över 50 000 kr ska först attesteras av driftchefen." },
  { roll: "agent", text: "Av de senaste 90 dagarnas 9 fakturor hade 7 fått ett annat förslag med den här regeln. Konteringen kan du godkänna själv. Attestregeln ändrar attestflödet och ska även godkännas av en andra ekonomiadministratör." },
];

// Tidslinje: varje bild säger hur mycket som syns från tidpunkten t (ms).
type Bild = { t: number; visade: number; skriv: string; tanker: boolean; kort: boolean; godkand: boolean; tryck: boolean };
function tidslinje(): Bild[] {
  const b: Bild[] = [];
  // Första meddelandet syns redan från start, så att rutan aldrig är tom innan samtalet spelar.
  let t = 0, visade = 1;
  const lagg = (d: Partial<Bild>) => { const f = { ...(b.at(-1) ?? { visade, skriv: "", tanker: false, kort: false, godkand: false, tryck: false }), ...d, t }; b.push(f); };
  lagg({}); t += 500;
  for (const r of RADER.slice(1)) {
    if (r.roll === "ekonom") {
      for (let i = 4; i <= r.text.length + 3; i += 4) { lagg({ skriv: r.text.slice(0, i) }); t += 30; }
      t += 250;
      visade++; lagg({ visade, skriv: "" }); t += 350;
    } else {
      lagg({ tanker: true }); t += 700;
      visade++; lagg({ visade, tanker: false }); t += 1500;
    }
  }
  lagg({ kort: true }); t += 1600;
  lagg({ tryck: true }); t += 250;
  lagg({ tryck: false, godkand: true });
  return b;
}

export function RegelchattDemo() {
  const bilder = useMemo(tidslinje, []);
  const slut = bilder[bilder.length - 1].t;
  const [tid, setTid] = useState(0);
  const [stilla, setStilla] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const synlig = useRef(false);
  const startad = useRef(false);

  useEffect(() => {
    const el = ref.current;
    const reducera = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!el || reducera || typeof IntersectionObserver === "undefined") { setStilla(true); setTid(slut); return; }
    // Spelar när panelen syns och står still utanför skärmen.
    const io = new IntersectionObserver(([e]) => {
      synlig.current = e.isIntersecting;
      if (e.isIntersecting) startad.current = true;
    }, { threshold: 0.3 });
    io.observe(el);
    const steg = 30;
    const id = window.setInterval(() => {
      if (!synlig.current || !startad.current || document.hidden) return;
      setTid((x) => (x >= slut ? x : x + steg));
    }, steg);
    return () => { io.disconnect(); window.clearInterval(id); };
  }, [slut]);

  let bild = bilder[0];
  for (const f of bilder) { if (f.t <= tid) bild = f; else break; }
  const klar = tid >= slut;

  return (
    <div ref={ref} className="fbana rcd">
      <div className="rcd-bar" aria-hidden="true">
        <span className="rcd-prick" />
        <span>Regelchatt</span>
        <span className="rcd-ex">Exempel</span>
      </div>

      <div className="rcd-logg" aria-hidden="true">
        {RADER.map((r, i) => (
          <div key={i} className={`rcd-b ${r.roll}` + (i < bild.visade ? " pa" : "")}>
            <span className="rcd-vem">{r.roll === "ekonom" ? "Ekonom" : "Agenten"}</span>
            <p>{r.text}</p>
          </div>
        ))}
        <div className={"rcd-kort" + (bild.kort ? " pa" : "") + (bild.godkand ? " ok" : "")}>
          <div className="rcd-kh"><b>Förslag i styrpanelen</b><span className="tag">Från chatten</span></div>
          <dl>
            <div><dt>Leverantör</dt><dd>{LEV} · alla fakturor</dd></div>
            <div><dt>Konto</dt><dd><span className="num">5170</span> Reparation och underhåll av fastighet</dd></div>
            <div><dt>Fördelningsnyckel</dt><dd className="rcd-fd">
              <span><span className="num">Ks 110</span><i style={{ ["--p" as string]: "60%" }} /><span className="num">60 %</span></span>
              <span><span className="num">Ks 120</span><i style={{ ["--p" as string]: "40%" }} /><span className="num">40 %</span></span>
            </dd></div>
            <div><dt>Attest</dt><dd>Över <span className="num">50 000</span> kr: driftchef, sedan ekonom</dd></div>
            <div><dt>Gäller från</dt><dd className="num">2026-11-01</dd></div>
          </dl>
          <div className="rcd-st">
            <span className={"rk-b p" + (bild.tryck ? " tryck" : "")}>Godkänn</span>
            <span className="rcd-vant">Väntar på godkännande</span>
            <span className="rcd-klar">
              <span className="tag gron">Kontering godkänd av ekonom</span>
              <span className="tag gul">Attestregeln väntar på en andra ekonomiadministratör</span>
            </span>
          </div>
        </div>
      </div>

      <div className="rcd-in">
        <span aria-hidden="true" className={"rcd-falt" + (bild.skriv ? " skriver" : "")}>
          {bild.tanker ? <span className="rcd-tanker">Agenten letar…</span>
            : bild.skriv ? <>{bild.skriv}<span className="rcd-mark" /></>
            : <span className="rcd-ph">Skriv en regel…</span>}
        </span>
        {klar && !stilla
          ? <button type="button" className="rcd-igen" onClick={() => setTid(0)}>Spela igen</button>
          : <span className="rcd-skicka" aria-hidden="true">Skicka</span>}
      </div>

      {/* Hela samtalet för skärmläsare, oberoende av animationen. */}
      <div className="sr-only">
        <p>Exempel på ett samtal i regelchatten.</p>
        <ol>
          {RADER.map((r, i) => <li key={i}>{r.roll === "ekonom" ? "Ekonom" : "Agenten"}: {r.text}</li>)}
          <li>Förslag i styrpanelen: {LEV}, alla fakturor, konto 5170, fördelningsnyckel 60 procent på kostnadsställe 110 och 40 procent på 120, attest över 50 000 kr av driftchef och sedan ekonom, gäller från 2026-11-01. Ekonomen godkänner konteringen. Attestregeln väntar på en andra ekonomiadministratör.</li>
        </ol>
      </div>
    </div>
  );
}
